import { css, cx, resetButtonStyle } from '@eti/styles';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { X as CloseIcon, Minus as MinimizeIcon, FrameCorners } from '@phosphor-icons/react';
import { useProperty, useSiteContext } from '@eti/providers';
import { useAuth } from '@eti/authentication';
import { breakpoints, Inline, Stack, vars } from '@etg/wings';
import type { ChatType } from '../models';
import {
  getChatbotOrianaAppearanceData,
  getIframeSrcUrl,
  setOrianaChatClosed,
  setOrianaChatOpen,
} from './utils';

const buttonStyles = css`
  ${resetButtonStyle};
  align-items: center;
  color: #fff;
  cursor: pointer;
  display: flex;
`;

const logoStyles = css`
  align-self: center;
  height: 35px;
  margin: 10px 16px;
  width: 35px;

  @media (min-width: ${breakpoints._768}) {
    height: 30px;
    margin: 0;
    padding: 4px;
    width: 30px;
  }
`;

const buttonsInlineStyles = css`
  margin-inline-end: 4px;

  @media (max-width: ${breakpoints._768}) {
    margin-inline-end: 8px;
  }
`;

const hideChatStyles = css`
  visibility: hidden;
`;

const stackStyles = (isExpanded: boolean) => css`
  bottom: 0;
  box-shadow: 16px 16px 80px rgba(0 0 0 / 50%);
  height: ${isExpanded ? '100%' : '50px'};
  overflow: hidden;
  position: fixed;
  right: 0;
  width: 100%;
  z-index: 90;

  @media (min-width: ${breakpoints._768}) {
    border-radius: ${vars.card.borderRadius};
    bottom: 24px;
    height: ${isExpanded ? '478px' : '30px'};
    right: 24px;
    width: 376px;
  }
`;

const iframeStyles = (isExpanded: boolean) => css`
  background: rgba(0 0 0 / 50%);
  flex: 1;
  height: ${isExpanded ? '100%' : '0'};
  opacity: ${isExpanded ? '1' : '0'};
  overflow: hidden;
  transition: opacity 1s ease-in-out;

  @media (max-width: ${breakpoints._768}) {
    height: ${isExpanded ? 'calc(100% - 50px)' : '0'};
  }
`;

const headerInlineStyles = (headerColor: string) => css`
  background-color: ${headerColor};
  justify-content: space-between;
`;

interface OrianaChatProps {
  isOpen: boolean;
  setOpenChatType: Dispatch<SetStateAction<ChatType | null>>;
}
const OrianaChat = ({ isOpen, setOpenChatType }: OrianaChatProps) => {
  const { brand, language } = useSiteContext();
  const { user } = useAuth();
  const { p } = useProperty();
  const siteLanguageCode = language?.code;
  const chatbotOrianaIframeSrc = p('Chatbot.Oriana.IframeSourceUrl');
  const chatbotOrianaDomainCode = p('Chatbot.Oriana.DomainCode');
  const { uiBundle, avatarFilename, headerColor } = useMemo(
    () => getChatbotOrianaAppearanceData(brand.code.toLowerCase()),
    [brand.code],
  );
  const [isExpanded, setIsExpanded] = useState(true);
  const [logo, setLogo] = useState('');
  const [iframeSrcUrl, setIframeSrcUrl] = useState('');

  useEffect(() => {
    const init = async () => {
      try {
        const { default: orianaAvatarIcon } = await import(
          `@eti/assets/images/oriana/${avatarFilename}`
        );
        setLogo(orianaAvatarIcon);
      } catch {
        setLogo('');
      }

      if (localStorage.getItem('isOrianaChatOpen') === 'true') {
        setOpenChatType('oriana');
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (!iframeSrcUrl && user?.email && user?.sessionOrderNumber) {
        setIframeSrcUrl(
          getIframeSrcUrl(
            uiBundle,
            user.email,
            user.sessionOrderNumber,
            window.location.hostname,
            siteLanguageCode,
            chatbotOrianaIframeSrc,
            chatbotOrianaDomainCode,
          ),
        );
      }
      setOrianaChatOpen();
    }
  }, [
    chatbotOrianaDomainCode,
    chatbotOrianaIframeSrc,
    iframeSrcUrl,
    isOpen,
    siteLanguageCode,
    uiBundle,
    user?.email,
    user?.sessionOrderNumber,
  ]);

  const handleCloseChat = () => {
    setOpenChatType(null);
    setOrianaChatClosed();
  };

  return (
    <Stack
      className={cx(stackStyles(isExpanded), !isOpen ? hideChatStyles : '')}
      data-testid="oriana-chat"
      id="orianaChat"
      spacing={0}
    >
      <Inline alignY="center" className={headerInlineStyles(headerColor)}>
        <img alt="logo" className={logoStyles} src={logo} />
        <Inline
          alignY="center"
          className={buttonsInlineStyles}
          spacing={{ [breakpoints._0]: 16, [breakpoints._768]: 8 }}
        >
          {isExpanded ? (
            <button
              className={buttonStyles}
              data-testid="oriana-chat-minimize-button"
              onClick={() => setIsExpanded(false)}
              type="button"
            >
              <MinimizeIcon alt="minimize" size={25} />
            </button>
          ) : (
            <button
              className={buttonStyles}
              data-testid="oriana-chat-expand-button"
              onClick={() => setIsExpanded(true)}
              type="button"
            >
              <FrameCorners alt="expand" size={25} />
            </button>
          )}
          <button
            className={buttonStyles}
            data-testid="oriana-chat-close-button"
            onClick={handleCloseChat}
            type="button"
          >
            <CloseIcon alt="close" size={25} />
          </button>
        </Inline>
      </Inline>
      <iframe
        allow="geolocation; microphone; camera"
        className={iframeStyles(isExpanded)}
        data-testid="oriana-chat-iframe"
        frameBorder="0"
        height="100%"
        id="receiver"
        src={iframeSrcUrl}
        title="Oriana"
        width="100%"
      >
        <p>Your browser does not support iframes.</p>
      </iframe>
    </Stack>
  );
};
export default OrianaChat;
